import { useState } from 'react'
import { useAppDispatch } from '../hooks/redux'
import { createStatus, fetchProjectStatuses } from '../store/slices/boardStatusSlice'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useToast } from './ToastContainer'

interface CreateStatusModalProps {
  isOpen: boolean
  onClose: () => void
  projectId: string
}

const PRESET_COLORS = [
  '#6b7280', // gray
  '#ef4444', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#06b6d4', // cyan
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#ec4899', // pink
]

const CreateStatusModal = ({ isOpen, onClose, projectId }: CreateStatusModalProps) => {
  const [name, setName] = useState('')
  const [color, setColor] = useState('#6b7280')
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const dispatch = useAppDispatch()
  const { showSuccess, showError } = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return

    setIsSubmitting(true)
    try {
      const result = await dispatch(createStatus({
        projectId,
        statusData: {
          name: name.trim(),
          color,
        }
      })).unwrap()
      
      console.log('新建狀態成功:', result)
      
      // 重新獲取狀態以確保同步
      await dispatch(fetchProjectStatuses(projectId))
      
      showSuccess('狀態建立成功')
      setName('')
      setColor('#6b7280')
      onClose()
    } catch (error: any) {
      console.error('建立狀態失敗:', error)
      showError('建立失敗', error.message || '無法建立狀態')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setName('')
    setColor('#6b7280')
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={handleClose} />
        
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">建立新狀態</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="statusName" className="block text-sm font-medium text-gray-700 mb-1">
                狀態名稱
              </label>
              <input
                id="statusName"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="輸入狀態名稱"
                maxLength={50}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                狀態顏色
              </label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((presetColor) => (
                  <button
                    key={presetColor}
                    type="button"
                    onClick={() => setColor(presetColor)}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      color === presetColor 
                        ? 'border-gray-400 scale-110' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    style={{ backgroundColor: presetColor }}
                  />
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isSubmitting}
              >
                取消
              </button>
              <button
                type="submit"
                disabled={!name.trim() || isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? '建立中...' : '建立狀態'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default CreateStatusModal